if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        if (this.dataset.videoAutoplay !== undefined) {
          this.initVideoAutoplay();
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const btn = mediaToSwitch.querySelector('button');
          btn.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          if (this.dataset.thumbnailHoverPreview !== undefined) {
            let hoverTimer;
            btn.addEventListener('mouseenter', () => {
              if (!this.mql.matches) return;
              clearTimeout(hoverTimer);
              hoverTimer = window.setTimeout(() => {
                this.setActiveMedia(mediaToSwitch.dataset.target, false);
              }, 80);
            });
          }
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        if (this.dataset.videoAutoplay === undefined || this.autoplayBootstrapped) return;
        this.autoplayBootstrapped = true;

        const bootstrap = () => this.syncActiveSlideFromScroll();
        requestAnimationFrame(bootstrap);
        window.setTimeout(bootstrap, 120);
        window.addEventListener('load', bootstrap, { once: true });
        window.addEventListener('pageshow', bootstrap);
      }

      initVideoAutoplay() {
        const viewer = this.elements.viewer;
        if (!viewer) return;

        const slider = viewer.querySelector('[id^="Slider-"]');
        if (slider) {
          slider.addEventListener(
            'scroll',
            debounce(() => {
              this.syncActiveSlideFromScroll();
            }, 120),
            { passive: true }
          );
        }

        viewer.addEventListener(
          'slideChanged',
          debounce((event) => {
            if (event.detail?.currentElement) {
              this.setActiveSlide(event.detail.currentElement);
            }
          }, 150)
        );

        if ('IntersectionObserver' in window) {
          this.videoObserver = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting && entry.intersectionRatio >= 0.55) {
                  this.setActiveSlide(entry.target);
                }
              });
            },
            { threshold: [0.55, 0.75] }
          );

          this.querySelectorAll('.product__media-item').forEach((item) => {
            if (item.querySelector('[data-autoplay-video]')) {
              this.videoObserver.observe(item);
            }
          });
        }
      }

      getVisibleMediaItem() {
        const items = this.elements.viewer?.querySelectorAll('.product__media-item');
        if (!items?.length) return null;

        let bestItem = null;
        let bestVisibleRatio = 0;

        items.forEach((item) => {
          const rect = item.getBoundingClientRect();
          const visibleWidth = Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0);
          if (item.offsetWidth <= 0) return;
          const ratio = visibleWidth / item.offsetWidth;
          if (ratio > bestVisibleRatio) {
            bestVisibleRatio = ratio;
            bestItem = item;
          }
        });

        return bestVisibleRatio >= 0.5 ? bestItem : null;
      }

      setActiveSlide(item) {
        if (!item) return;

        this.elements.viewer.querySelectorAll('.product__media-item').forEach((element) => {
          element.classList.toggle('is-active', element === item);
        });

        this.playActiveMedia(item);
      }

      syncActiveSlideFromScroll() {
        const visibleItem =
          this.getVisibleMediaItem() ||
          this.elements.viewer.querySelector('.product__media-item.is-active') ||
          this.elements.viewer.querySelector('.product__media-item');

        if (visibleItem) this.setActiveSlide(visibleItem);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);

        if (this.dataset.videoAutoplay !== undefined) {
          this.setActiveSlide(event.detail.currentElement);
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        const position = activeThumbnail?.dataset?.mediaPosition;
        if (position) this.announceLiveRegion(activeMedia, position);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      pauseGalleryMedia() {
        this.querySelectorAll('video').forEach((video) => video.pause());
        this.querySelectorAll('.js-youtube').forEach((iframe) => {
          iframe.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
        });
        this.querySelectorAll('.js-vimeo').forEach((iframe) => {
          iframe.contentWindow.postMessage('{"method":"pause"}', '*');
        });
      }

      playActiveMedia(activeItem) {
        if (!activeItem) return;

        this.pauseGalleryMedia();

        const inlineVideo = activeItem.querySelector('.product__inline-autoplay-video video');
        if (inlineVideo) {
          inlineVideo.muted = true;
          inlineVideo.playsInline = true;
          inlineVideo.setAttribute('muted', '');
          inlineVideo.setAttribute('playsinline', '');
          const playPromise = inlineVideo.play();
          if (playPromise?.catch) playPromise.catch(() => {});
          return;
        }

        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (!deferredMedia) return;

        if (this.dataset.videoAutoplay !== undefined && !deferredMedia.hasAttribute('data-autoplay-video')) {
          return;
        }

        deferredMedia.loadContent(false);

        const video = deferredMedia.querySelector('video');
        if (!video) return;

        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');

        const tryPlay = () => {
          const playPromise = video.play();
          if (playPromise?.catch) {
            playPromise.catch(() => {
              window.setTimeout(() => video.play().catch(() => {}), 300);
            });
          }
        };

        if (video.readyState >= 2) {
          tryPlay();
        } else {
          video.addEventListener('loadeddata', tryPlay, { once: true });
          video.addEventListener('canplay', tryPlay, { once: true });
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
